import React, { useEffect, useMemo, useState } from 'react';
import { SearchSelect } from '../pages/review/reviewUi';
import { sbomApi } from './sbomApi';
import { suggestFieldMap } from './sbomFieldMatch';
import '../styles/DocReview.css';
import './Sbom.css';

const projectLabel = (p) => `${p.project_name}${p.is_solution ? ' (Solution)' : ''}${p.version_label ? ` · ${p.version_label}` : ''}`;
const az = (a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

/**
 * Admin → Settings → Software BOM (v4.30.0). Saved to `sbom_settings` through
 * `/api/bom/sbom/settings` — never to the hardware BOM's settings (design/DESIGN_SBOM.md §4).
 *
 * Pickers are searchable, A→Z, Solution projects in bold (the platform rule for project / type /
 * filter pickers).
 */
export default function SbomSettingsForm() {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(null);
  const [projects, setProjects] = useState(null);
  const [filters, setFilters] = useState({});        // type code → [{id, name}] | 'loading' | 'failed'
  const [fields, setFields] = useState(null);        // component form fields, once configured
  const [fieldsError, setFieldsError] = useState('');
  const [relFields, setRelFields] = useState(null);  // release form fields, for the version field
  const [suggested, setSuggested] = useState({});   // role → true while its field is an unsaved automatic match
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const [busy, setBusy] = useState(false);
  const [riskPanels, setRiskPanels] = useState(null);   // [{id, name}] | {error}

  const adopt = (d) => {
    setInfo(d);
    const s = d.settings;
    setForm({
      project_id: s.projectId, version_id: s.versionId, release_type: s.releaseType, release_label: s.releaseLabel,
      release_filter_id: s.releaseFilterId, component_type: s.componentType, component_label: s.componentLabel,
      component_filter_id: s.componentFilterId, contains_link: s.containsLink,
      release_version_field: s.releaseVersionField, release_link_field: s.releaseLinkField || '', field_map: { ...(s.fieldMap || {}) },
      osv_enabled: s.osvEnabled !== false, kev_enabled: s.kevEnabled !== false, daily_enabled: s.dailyEnabled !== false,
      risk_setup_id: s.riskSetupId || '', vuln_type: s.vulnType || '', vuln_label: s.vulnLabel || '',
    });
    setSuggested({});
  };

  /** Fill the given roles with a field matched by name; roles mapped elsewhere keep their field. */
  const autoMatch = (roleKeys) => {
    if (!fields?.length || !form || !roleKeys.length) return 0;
    const keep = Object.entries(form.field_map).filter(([r]) => !roleKeys.includes(r)).map(([, t]) => t);
    const found = suggestFieldMap(roleKeys, fields, keep);
    const n = Object.keys(found).length;
    if (n) {
      setForm((f) => ({ ...f, field_map: { ...f.field_map, ...found } }));
      setSuggested((x) => ({ ...x, ...Object.fromEntries(Object.keys(found).map((r) => [r, true])) }));
      setSaved('');
    }
    return n;
  };

  useEffect(() => {
    sbomApi.settings().then(adopt).catch((e) => setError(e.message));
    fetch('/api/config/projects', { credentials: 'same-origin' })
      .then((r) => r.json()).then((d) => setProjects(Array.isArray(d.projects) ? d.projects : []))
      .catch(() => setProjects([]));
    fetch('/api/risk/panels', { credentials: 'same-origin', cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setRiskPanels((d.panels || []).map((p) => ({ id: String(p.id), name: p.name || p.id }))))
      .catch((st) => setRiskPanels({ error: st === 403 ? 'The Risk module is not enabled for you, so no Risk panel can be chosen.'
        : 'The Risk panels could not be read.' }));
  }, []);

  const configured = !!info?.configured;
  useEffect(() => {
    if (!configured) return;
    sbomApi.formFields('component').then((d) => { setFields(d.fields || []); setFieldsError(''); })
      .catch((e) => { setFields([]); setFieldsError(e.message); });
    sbomApi.formFields('release').then((d) => setRelFields(d.fields || [])).catch(() => setRelFields([]));
  }, [configured, info]);

  // A role never set (absent from the saved map) gets a default match by field name. A role saved
  // as "Not mapped" is kept as an empty title by the server, so it is not matched again.
  useEffect(() => {
    if (!fields?.length || !info?.can_edit || !form) return;
    autoMatch(info.roles.map((r) => r.key).filter((k) => !(k in form.field_map)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  const project = useMemo(() => (projects || []).find((p) => String(p.version_id) === String(form?.version_id)), [projects, form]);
  const types = useMemo(() => (project?.item_types || []).filter((t) => t.code)
    .map((t) => ({ value: String(t.code).toUpperCase(), label: `${t.label || t.code} (${t.code})`, raw: t })).sort(az), [project]);

  const loadFilters = (code) => {
    if (!form?.project_id || !code || filters[`${form.project_id}|${code}`]) return;
    const k = `${form.project_id}|${code}`;
    setFilters((f) => ({ ...f, [k]: 'loading' }));
    fetch(`/api/config/filters?project_id=${encodeURIComponent(form.project_id)}&item_type=${encodeURIComponent(code)}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setFilters((f) => ({ ...f, [k]: d.filters || [] })))
      .catch(() => setFilters((f) => ({ ...f, [k]: 'failed' })));
  };
  useEffect(() => { if (form) { loadFilters(form.release_type); loadFilters(form.component_type); } });

  if (error && !form) return <div className="sbom-error">{error}</div>;
  if (!form) return <div className="sbom-muted">Loading…</div>;
  const canEdit = !!info?.can_edit;
  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setSaved(''); };

  const projectOptions = (projects || []).map((p) => ({ value: String(p.version_id), label: projectLabel(p), strong: !!p.is_solution })).sort(az);
  const filterOptions = (code) => {
    const list = filters[`${form.project_id}|${code}`];
    return Array.isArray(list) ? list.map((f) => ({ value: String(f.id), label: `${f.name} (#${f.id})` })).sort(az) : [];
  };
  const filterState = (code) => filters[`${form.project_id}|${code}`];
  const relOptions = [{ value: '', label: 'None' }, ...(relFields || []).map((f) => ({ value: f.title, label: f.title })).sort(az)];
  const fieldOptions = [{ value: '', label: 'Not mapped' }, ...(fields || []).map((f) => ({ value: f.title, label: f.title })).sort(az)];

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const d = await sbomApi.saveSettings(form);
      // A backend older than this form drops unknown fields with a 200 (Critical Note #64).
      if (d.settings.releaseType !== form.release_type || d.settings.componentType !== form.component_type) {
        setError('The server did not keep the work item types — restart the backend and save again.');
      } else {
        adopt(d);
        setSaved('Saved. The hardware BOM settings were not changed.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="bom-scope sbom-settings" onSubmit={save}>
      <div>
        <h3 style={{ margin: 0 }}>Software BOM (SBOM)</h3>
        <p className="sbom-tiny sbom-muted" style={{ margin: '4px 0 0' }}>
          Where software releases and their components live in Orcanos. Saved separately from the BOM Viewer settings.
        </p>
      </div>
      {!canEdit && <div className="sbom-note">Only an Orcanos administrator can change these settings.</div>}

      <div className="sbom-panel">
        <h4>Project</h4>
        {projects === null ? <div className="sbom-tiny sbom-muted">Loading projects…</div> : (
          <SearchSelect options={projectOptions} value={form.version_id} disabled={!canEdit} ariaLabel="Project"
            placeholder="Search projects…"
            onChange={(v) => {
              const p = (projects || []).find((x) => String(x.version_id) === String(v));
              set({ version_id: String(v || ''), project_id: p ? String(p.project_id) : '' });
            }} />
        )}
      </div>

      <div className="sbom-panel">
        <h4>Work item types and filters</h4>
        <div className="sbom-grid2">
          <label className="sbom-field">Software release type (the top of each tree)
            <SearchSelect options={types} value={form.release_type} disabled={!canEdit || !project} ariaLabel="Release type"
              placeholder={project ? 'Search work items…' : 'Choose the project first'}
              onChange={(v) => { const t = types.find((x) => x.value === v); set({ release_type: v, release_label: t?.raw.label || '', release_filter_id: '' }); }} />
          </label>
          <label className="sbom-field">Release filter (optional: lists releases that have no SBOM yet)
            <FilterRow state={filterState(form.release_type)} options={filterOptions(form.release_type)} canEdit={canEdit} code={form.release_type} value={form.release_filter_id} what="Release" onChange={(v) => set({ release_filter_id: v })} />
          </label>
          <label className="sbom-field">Software component type
            <SearchSelect options={types} value={form.component_type} disabled={!canEdit || !project} ariaLabel="Component type"
              placeholder={project ? 'Search work items…' : 'Choose the project first'}
              onChange={(v) => { const t = types.find((x) => x.value === v); set({ component_type: v, component_label: t?.raw.label || '', component_filter_id: '' }); }} />
          </label>
          <label className="sbom-field">Component filter (optional: shows values edited in Orcanos)
            <FilterRow state={filterState(form.component_type)} options={filterOptions(form.component_type)} canEdit={canEdit} code={form.component_type} value={form.component_filter_id} what="Component" onChange={(v) => set({ component_filter_id: v })} />
          </label>
          <label className="sbom-field">Release field that holds its version (optional)
            {relFields && relFields.length > 0 && form.release_type === info.settings.releaseType ? (
              <SearchSelect options={relOptions} value={form.release_version_field || ''} disabled={!canEdit} ariaLabel="Release version field"
                onChange={(v) => set({ release_version_field: v })} />
            ) : (
              <input className="sbom-input" value={form.release_version_field} disabled={!canEdit}
                placeholder={configured ? 'Save first to choose from the release form' : 'Save the types first'}
                onChange={(e) => set({ release_version_field: e.target.value })} />
            )}
            {relFields && form.release_version_field && form.release_type === info.settings.releaseType
              && !relFields.some((f) => f.title.toLowerCase() === form.release_version_field.toLowerCase()) && (
              <span className="sbom-miss">&ldquo;{form.release_version_field}&rdquo; is not a field of the {form.release_label || 'release'} form. Choose another or None.</span>
            )}
          </label>
          <label className="sbom-field">Release field for the SBOM link (optional: opens the release here from Orcanos)
            {relFields && relFields.length > 0 && form.release_type === info.settings.releaseType ? (
              <SearchSelect options={relOptions} value={form.release_link_field || ''} disabled={!canEdit} ariaLabel="Release SBOM link field"
                onChange={(v) => set({ release_link_field: v })} />
            ) : (
              <input className="sbom-input" value={form.release_link_field} disabled={!canEdit}
                placeholder={configured ? 'Save first to choose from the release form' : 'Save the types first'}
                onChange={(e) => set({ release_link_field: e.target.value })} />
            )}
            {relFields && form.release_link_field && form.release_type === info.settings.releaseType
              && !relFields.some((f) => f.title.toLowerCase() === form.release_link_field.toLowerCase()) && (
              <span className="sbom-miss">&ldquo;{form.release_link_field}&rdquo; is not a field of the {form.release_label || 'release'} form. Choose another or None.</span>
            )}
          </label>
        </div>
        {form.release_type && form.release_type === form.component_type && (
          <div className="sbom-error">The release and the component must be two different work item types.</div>
        )}
      </div>

      <div className="sbom-panel">
        <h4>Component fields</h4>
        <div className="sbom-tiny sbom-muted">Which field of the {form.component_label || 'component'} form receives each value
          from the file. Name and Description always go to the item&rsquo;s own name and description.</div>
        {!configured && <div className="sbom-note">Save the project and types first; the form&rsquo;s fields are read from Orcanos then.</div>}
        {fieldsError && <div className="sbom-warn">Could not read the form: {fieldsError}</div>}
        {configured && fields && Object.keys(suggested).length > 0 && (
          <div className="sbom-note">Fields marked <b>Suggested</b> were matched by name. Check them, then Save.</div>
        )}
        {configured && fields && (
          <div className="sbom-tw">
            <table className="sbom-table">
              <thead><tr><th>Value from the SBOM</th><th>Orcanos field</th></tr></thead>
              <tbody>
                {info.roles.map((r) => (
                  <tr key={r.key}>
                    <td>{r.label}{r.key === 'purl' && <span className="sbom-tiny sbom-muted"> (recommended)</span>}</td>
                    <td style={{ minWidth: 260 }}>
                      <div className={`sbom-map-cell${form.field_map[r.key] ? '' : ' sbom-map-cell--unmapped'}`}>
                        <SearchSelect options={fieldOptions} value={form.field_map[r.key] || ''} disabled={!canEdit}
                          ariaLabel={`Field for ${r.label}`} placeholder="Not mapped"
                          onChange={(v) => {
                            set({ field_map: { ...form.field_map, [r.key]: v } });
                            setSuggested((x) => { const rest = { ...x }; delete rest[r.key]; return rest; });
                          }} />
                        {suggested[r.key] && <span className="sbom-suggested">Suggested</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {configured && fields?.length > 0 && canEdit && (
          <div className="sbom-actions">
            <button type="button" className="sbom-btn"
              title="Match every row that is Not mapped to a field with a similar name"
              onClick={() => {
                if (!autoMatch(info.roles.map((r) => r.key).filter((k) => !form.field_map[k]))) {
                  setSaved('No more fields could be matched by name.');
                }
              }}>Match automatically</button>
          </div>
        )}
        <div className="sbom-note">The package URL is also written to the item&rsquo;s Migration Reference, so every component
          can be found again in Orcanos even with no field mapped.</div>
      </div>

      <div className="sbom-panel">
        <h4>Vulnerabilities and risk</h4>
        <div className="sbom-tiny sbom-muted">Each component is checked by its package URL. Only the package name and version leave
          the server.</div>
        <div className="sbom-grid2">
          <div className="sbom-col">
            <label className="sbom-check"><input type="checkbox" checked={form.osv_enabled} disabled={!canEdit}
              onChange={(e) => set({ osv_enabled: e.target.checked })} /> Check OSV.dev (by package and version)</label>
            <label className="sbom-check"><input type="checkbox" checked={form.kev_enabled} disabled={!canEdit || !form.osv_enabled}
              onChange={(e) => set({ kev_enabled: e.target.checked })} /> Flag CISA Known Exploited Vulnerabilities</label>
            <label className="sbom-check"><input type="checkbox" checked={form.daily_enabled} disabled={!canEdit || !form.osv_enabled}
              onChange={(e) => set({ daily_enabled: e.target.checked })} /> Check every release again daily</label>
          </div>
          <div className="sbom-col">
            <label className="sbom-field">Risk panel for linking affected vulnerabilities
              {Array.isArray(riskPanels) ? (
                <SearchSelect options={[{ value: '', label: 'None' }, ...riskPanels.map((p) => ({ value: p.id, label: p.name })).sort(az)]}
                  value={form.risk_setup_id} disabled={!canEdit} ariaLabel="Risk panel" placeholder="Search Risk panels…"
                  onChange={(v) => set({ risk_setup_id: String(v || '') })} />
              ) : riskPanels?.error ? <span className="sbom-tiny sbom-muted">{riskPanels.error}</span>
                : <span className="sbom-tiny sbom-muted">Loading Risk panels…</span>}
            </label>
            <label className="sbom-field">Vulnerability work item type (optional: each assessment is also saved as an Orcanos item)
              <SearchSelect options={[{ value: '', label: 'None' }, ...types.filter((t) => t.value !== form.release_type && t.value !== form.component_type)]}
                value={form.vuln_type} disabled={!canEdit || !project} ariaLabel="Vulnerability type"
                placeholder={project ? 'Search work items…' : 'Choose the project first'}
                onChange={(v) => { const t = types.find((x) => x.value === v); set({ vuln_type: v || '', vuln_label: t?.raw.label || '' }); }} />
            </label>
          </div>
        </div>
        <div className="sbom-note">An <b>Affected</b> vulnerability is linked to a risk of the chosen panel, and its control status
          (mitigation → test) comes from Risk Management. Without a panel, verdicts are saved without a risk.</div>
      </div>

      {error && <div className="sbom-error" role="alert">{error}</div>}
      {saved && <div className="sbom-note" role="status">{saved}</div>}
      {canEdit && (
        <div className="sbom-actions" style={{ justifyContent: 'flex-end' }}>
          <button type="submit" className="sbom-btn sbom-btn--pri" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      )}
    </form>
  );
}

/** A saved-filter picker for one type, or a plain id box when Orcanos lists none. */
function FilterRow({ code, value, onChange, what, state, options, canEdit }) {
  if (!code) return <div className="sbom-tiny sbom-muted">Choose the work item type first.</div>;
  if (state === 'loading') return <div className="sbom-tiny sbom-muted">Loading filters…</div>;
  if (!Array.isArray(state) || !state.length) {
    return (
      <input className="sbom-input" value={value} inputMode="numeric" placeholder={`${what} filter id`} disabled={!canEdit}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))} />
    );
  }
  return <SearchSelect options={options} value={value} onChange={onChange} disabled={!canEdit}
    placeholder="Search filters…" ariaLabel={`${what} filter`} />;
}
